/**
 * E-Mail-Vorlagen-Verwaltung
 *
 * Route: /email-vorlagen (Admin-Rollen)
 *
 * Layout: 3-Spalten
 *  - Links: Liste aller Vorlagen (System + eigene), aktive markiert
 *  - Mitte: Formular zum Bearbeiten (Farben, Fonts, Header/Footer, Anlagenhinweis)
 *  - Rechts: Live-HTML-Preview via /api/email-templates/preview
 *
 * System-Vorlagen sind schreibgeschützt — user kann sie aber als Default setzen
 * oder eine editierbare Kopie erstellen ("Duplizieren").
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, Star, Trash, FloppyDisk, Plus, EnvelopeSimple } from "@phosphor-icons/react";

const HEADER_STYLES = [
  { value: "logo_left", label: "Logo/Firmenname links (Standard)" },
  { value: "logo_center", label: "Zentriert" },
  { value: "banner", label: "Farbiges Banner" },
  { value: "minimal", label: "Minimal (klein & dezent)" },
  { value: "text_only", label: "Kein Header" },
];

const FONTS = [
  { value: "Arial, 'Helvetica Neue', Helvetica, sans-serif", label: "Arial / Helvetica" },
  { value: "'Segoe UI', Roboto, Arial, sans-serif", label: "Segoe UI / Roboto" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia (Serif)" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const active = templates.find((t) => t.id === activeId);
  const isSystem = !!active?.is_system;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data);
      if (!activeId && data.length) {
        setActiveId(data.find((t) => t.is_default)?.id || data[0].id);
      }
    } catch (e) {
      toast.error("Vorlagen konnten nicht geladen werden");
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (active) setForm({ ...active });
  }, [active]);

  // Live-Preview (debounced)
  useEffect(() => {
    if (!form) return;
    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const payload = { ...form };
        delete payload.id; delete payload.tenant_id;
        delete payload.is_system; delete payload.created_at;
        delete payload.updated_at;
        const { data } = await api.post("/email-templates/preview", payload);
        setPreviewHtml(data.html);
      } catch (e) {
        // Preview soft-fail
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form]);

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form || isSystem) return;
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.tenant_id;
      delete payload.is_system; delete payload.created_at;
      delete payload.updated_at;
      await api.patch(`/email-templates/${form.id}`, payload);
      toast.success("Vorlage gespeichert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (tpl) => {
    try {
      const payload = { ...tpl, is_default: true };
      delete payload.id; delete payload.tenant_id;
      delete payload.is_system; delete payload.created_at;
      delete payload.updated_at;
      await api.patch(`/email-templates/${tpl.id}`, payload);
      toast.success(`„${tpl.name}" ist jetzt Standard-Vorlage`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umstellung fehlgeschlagen");
    }
  };

  const handleDuplicate = async (tpl) => {
    try {
      const { data } = await api.post(`/email-templates/${tpl.id}/duplicate`);
      toast.success(`Kopie erstellt: „${data.name}"`);
      await load();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplizieren fehlgeschlagen");
    }
  };

  const handleDelete = async (tpl) => {
    if (!await window._domivusConfirm(`Vorlage „${tpl.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/email-templates/${tpl.id}`);
      toast.success("Vorlage gelöscht");
      await load();
      if (activeId === tpl.id) setActiveId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleResync = async () => {
    if (!await window._domivusConfirm("System-Vorlagen (Klassisch/Modern/Minimal) auf die aktuellen Code-Defaults zurücksetzen? Eigene Vorlagen bleiben unverändert.")) return;
    try {
      const { data } = await api.post("/email-templates/system/resync");
      toast.success(`System-Vorlagen aktualisiert (${data.updated?.length || 0}).`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="email-templates-page">
      <div className="flex items-center gap-3 mb-6">
        <EnvelopeSimple size={26} className="text-slate-700" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">E-Mail-Layouts</h1>
          <p className="text-sm text-slate-500">Passen Sie das Aussehen der versendeten Mails an. Firmen- und Kontaktdaten kommen automatisch aus den Stammdaten.</p>
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={handleResync} data-testid="tpl-resync-system">
            System-Vorlagen aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Liste */}
        <aside className="col-span-3 space-y-2" data-testid="tpl-list">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveId(t.id)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                activeId === t.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-800 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`tpl-item-${t.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold flex-1 truncate">{t.name}</div>
                {t.is_default && <Star size={14} weight="fill" className="text-amber-400" />}
                {t.is_system && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">System</span>}
              </div>
              <div className={`text-[11px] mt-0.5 ${activeId === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.variant}</div>
            </button>
          ))}
        </aside>

        {/* Editor */}
        <section className="col-span-5 bg-white border border-slate-200 rounded-lg p-5 space-y-5" data-testid="tpl-editor">
          {!form ? (
            <div className="text-sm text-slate-500 italic">Bitte eine Vorlage aus der Liste wählen.</div>
          ) : (
            <>
              {isSystem && (
                <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded p-2.5">
                  Diese Vorlage ist eine System-Vorlage und schreibgeschützt.
                  Klicken Sie auf „Duplizieren" um eine editierbare Kopie zu erstellen.
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-wider">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => updateForm("name", e.target.value)}
                    disabled={isSystem}
                    data-testid="tpl-name"
                  />
                </div>
                <div className="flex items-end gap-1 pt-4">
                  {!active?.is_default && (
                    <Button variant="outline" size="sm" onClick={() => handleSetDefault(active)} data-testid="tpl-set-default">
                      <Star size={14} className="mr-1" /> Als Standard
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleDuplicate(active)} data-testid="tpl-duplicate">
                    <Copy size={14} className="mr-1" /> Duplizieren
                  </Button>
                  {!isSystem && (
                    <Button variant="outline" size="sm" onClick={() => handleDelete(active)}
                            className="text-red-600 hover:text-red-700" data-testid="tpl-delete">
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </div>

              <FieldGroup label="Farben">
                <ColorRow label="Primärfarbe (Header)" v={form.primary_color} onChange={(v) => updateForm("primary_color", v)} disabled={isSystem} testid="tpl-primary" />
                <ColorRow label="Akzentfarbe (Links)" v={form.accent_color} onChange={(v) => updateForm("accent_color", v)} disabled={isSystem} testid="tpl-accent" />
                <ColorRow label="Hintergrund" v={form.background_color} onChange={(v) => updateForm("background_color", v)} disabled={isSystem} testid="tpl-bg" />
                <ColorRow label="Text" v={form.text_color} onChange={(v) => updateForm("text_color", v)} disabled={isSystem} testid="tpl-text" />
              </FieldGroup>

              <FieldGroup label="Typografie">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Schriftart Fließtext</Label>
                  <Select value={form.font_family} onValueChange={(v) => updateForm("font_family", v)} disabled={isSystem}>
                    <SelectTrigger data-testid="tpl-font-body"><SelectValue /></SelectTrigger>
                    <SelectContent>{FONTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Schriftgröße Fließtext</Label>
                  <Input type="number" min={12} max={20} value={form.body_font_size}
                    onChange={(e) => updateForm("body_font_size", parseInt(e.target.value || 15, 10))}
                    disabled={isSystem} data-testid="tpl-font-size" />
                </div>
              </FieldGroup>

              <FieldGroup label="Header">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Header-Stil</Label>
                  <Select value={form.header_style} onValueChange={(v) => updateForm("header_style", v)} disabled={isSystem}>
                    <SelectTrigger data-testid="tpl-header-style"><SelectValue /></SelectTrigger>
                    <SelectContent>{HEADER_STYLES.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <SwitchRow label="Firmenname anzeigen" v={form.show_company_name} onChange={(v) => updateForm("show_company_name", v)} disabled={isSystem} />
                <SwitchRow label="Adresse anzeigen" v={form.show_company_address} onChange={(v) => updateForm("show_company_address", v)} disabled={isSystem} />
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Eigener Header-HTML (optional)</Label>
                  <Textarea rows={2} value={form.custom_header_html || ""} onChange={(e) => updateForm("custom_header_html", e.target.value)}
                    placeholder="&lt;div style=&quot;...&quot;&gt;Eigener HTML-Header&lt;/div&gt;"
                    disabled={isSystem} className="font-mono text-xs" />
                </div>
              </FieldGroup>

              <FieldGroup label="Anlagenhinweis">
                <SwitchRow label="Hinweis auf Anhänge anzeigen" v={form.show_attachment_hint} onChange={(v) => updateForm("show_attachment_hint", v)} disabled={isSystem} />
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Text bei 1 Anhang</Label>
                  <Input value={form.attachment_hint_singular} onChange={(e) => updateForm("attachment_hint_singular", e.target.value)} disabled={isSystem} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Text bei mehreren Anhängen (Platzhalter: &#123;count&#125;)</Label>
                  <Input value={form.attachment_hint_plural} onChange={(e) => updateForm("attachment_hint_plural", e.target.value)} disabled={isSystem} />
                </div>
              </FieldGroup>

              <FieldGroup label="Footer">
                <SwitchRow label="Signatur mit Sachbearbeiter" v={form.show_signature} onChange={(v) => updateForm("show_signature", v)} disabled={isSystem} />
                <SwitchRow label="Impressum-Daten (GF, USt-Id) im Footer" v={form.show_footer_impressum} onChange={(v) => updateForm("show_footer_impressum", v)} disabled={isSystem} />
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Footer-Hinweis</Label>
                  <Input value={form.footer_notice || ""} onChange={(e) => updateForm("footer_notice", e.target.value)} disabled={isSystem} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Eigener Footer-HTML (optional)</Label>
                  <Textarea rows={2} value={form.custom_footer_html || ""} onChange={(e) => updateForm("custom_footer_html", e.target.value)} disabled={isSystem} className="font-mono text-xs" />
                </div>
              </FieldGroup>

              {!isSystem && (
                <div className="pt-3 border-t border-slate-200">
                  <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="tpl-save">
                    <FloppyDisk size={14} className="mr-2" />
                    {saving ? "Speichere…" : "Änderungen speichern"}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Preview */}
        <section className="col-span-4 sticky top-4 self-start" data-testid="tpl-preview">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
            <span>Live-Vorschau</span>
            {previewLoading && <span className="text-slate-400">aktualisiert…</span>}
          </div>
          <iframe
            title="Preview"
            srcDoc={previewHtml || "<p style='padding:40px;font-family:sans-serif;color:#94a3b8'>Vorschau wird erstellt…</p>"}
            className="w-full h-[720px] bg-white border border-slate-200 rounded-md"
            data-testid="tpl-preview-iframe"
          />
        </section>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="border-t border-slate-100 pt-4 space-y-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SwitchRow({ label, v, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={!!v} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function ColorRow({ label, v, onChange, disabled, testid }) {
  return (
    <div className="flex items-center gap-2">
      <label className="flex-1 text-sm text-slate-700">{label}</label>
      <input type="color" value={v} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-10 h-8 rounded border border-slate-200 cursor-pointer disabled:opacity-50" />
      <Input value={v} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-24 font-mono text-xs" data-testid={testid} />
    </div>
  );
}
